/**
 * 다각형 패널 엔진
 *
 * 평면 = 패널 바깥면을 잇는 직각 다각형 (하리 = 안쪽 빈 공간만큼 파인 홈, 코너 하리 = 모서리 홈)
 * 패널 = 각 변을 따라 안쪽으로 두께 PT만큼 붙는 띠
 * 꼭짓점마다 PT×PT 코너 칸: 볼록이면 두 띠가 겹치는 칸, 오목이면 두 띠 사이 빈 칸
 *
 * 꺾임 규칙 (단 하나): 흐름(CW/CCW) 방향으로 다음 변이 코너 칸을 소유하고 코너로 시작,
 *                      앞 변은 그 칸 면에서 끝나며 양수로 꽂힘
 * → 띠 시작: 볼록 0 / 오목 −PT(칸까지 뒤로 연장),  띠 끝: 볼록 −PT(칸 앞) / 오목 0
 */
import { Hari } from '../types';
import { SegRule, fillSegment } from '../utils/segmentFill';

export type Pt = { x: number; y: number };
export type Flow = 'cw' | 'ccw';
export type EndType = 'corner' | 'yangsu' | 'eumsu' | 'none'; // none = 문 등 개구부에 닿는 평평한 끝

export interface PolyPanel {
  id: string;          // 안정 ID = 조각 키 + ':' + 오름차순 순번 (흐름·패널 수가 바뀌어도 다른 조각과 안 엉킴)
  pieceKey: string;    // 조각 키 = 변 위치(양 끝 좌표) + '#' + 변 안 조각 순번 (문 등으로 나뉜 조각)
  ascIndex: number;    // 조각 안 순번 (가로 왼→오, 세로 위→아래)
  edge: number;        // 변 번호 (흐름 순서)
  a: Pt; b: Pt;        // 중심선 시작·끝 (흐름 방향)
  size: number;        // 패널 길이 (mm)
  startType: EndType;  // 흐름 시작 끝
  endType: EndType;    // 흐름 끝 끝
  inDir?: Pt;          // startType=corner일 때 코너 칸으로 들어오는 앞 변의 흐름 방향 (코너 삼각형 방향)
  thick?: number;      // 패널 두께 (칸막이 등 PT와 다를 때)
}

/** 사용자 편집: 조각별 채우기 규칙(크기 고정 포함) + 접합 뒤집기 (키 = 조각 키 + ':j' + 오름차순 접합 순번) */
export interface PolyEdits { rules?: Record<string, SegRule>; flips?: Record<string, boolean> }
/** 조각 정보 (편집 창용) */
export interface PieceInfo { key: string; total: number; sizes: number[]; horiz: boolean; error?: string }

/**
 * 개구부: 띠에서 [from, to] 구간(세계 좌표, 가로변은 x·세로변은 y)을 비움
 * - 대상: side(외곽 경계 면의 외벽) 또는 edge(변 키 'x1,y1-x2,y2', 홈 안쪽 변 등)
 * - kind: door(문 — 맞닿는 끝 평평) / existing(기존 벽 — 패널 없음, 맞닿는 끝 음수, 꼭짓점이면 이웃 변이 끝까지 옴)
 */
export interface Opening {
  side?: 'top' | 'right' | 'bottom' | 'left';
  edge?: string;
  from: number; to: number;
  kind?: 'door' | 'existing';
  id?: string; // 문 id (결과 매핑용)
  /** 문 양 끝 타입 (세계 좌표 기준 lo = 왼/위, hi = 오른/아래). 없으면 자동 — 흐름 속 패널처럼 들어오는 쪽 음수·나가는 쪽 양수 */
  lo?: 'yangsu' | 'eumsu';
  hi?: 'yangsu' | 'eumsu';
}

/** 변 종류: 외벽은 좌표 오름차순으로 채움(나머지가 오름차순 끝), 하리 안쪽 줄은 흐름 순서로 채움 */
export type EdgeKind = 'wall' | 'hari';
/** 다각형: 꼭짓점(시계방향) + 각 꼭짓점에서 시작하는 변의 종류 */
export interface Outline { pts: Pt[]; kinds: EdgeKind[] }

export interface FillOpts { max: number; min: number; half: number; space: number }

/** 분할 규칙 (panelLayout.fillWithPanels와 동일): 900 최대 활용, 나머지 200 미만이면 반절 전략,
 *  접합 n×4 — 1장이어도 간격 4 (이전 시스템 실제 기록 기준, 2026-10-24) */
export function fillLength(length: number, o: FillOpts): number[] {
  if (length <= 0) return [];
  if (length < o.min + o.space) return [o.min];
  if (length <= o.max + o.space) return [length - o.space];
  const sizes: number[] = [];
  let remaining = length;
  while (remaining > o.max + o.space) { sizes.push(o.max); remaining -= o.max + o.space; }
  remaining -= o.space;
  if (remaining >= o.min) { sizes.push(remaining); return sizes; }
  if (sizes.length > 0) {
    sizes[sizes.length - 1] = o.half;
    remaining += o.max - o.half;
    if (remaining > o.max) { sizes.push(o.max); remaining -= o.max + o.space; }
    if (remaining > 0) sizes.push(remaining);
  } else sizes.push(remaining);
  return sizes;
}

/**
 * 홈: 직사각형에서 파낸 부분 (평면 모양의 잘라낸 부분 = 외벽 홈, 하리 빈 공간 = 하리 홈)
 * - corner: 모서리에서 w(가로) × h(세로)
 * - side: 면의 [from, to] 구간을 depth만큼 안쪽으로
 */
export type Corner = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
export type Side = 'top' | 'right' | 'bottom' | 'left';
export type Notch =
  | { type: 'corner'; corner: Corner; w: number; h: number; kind: EdgeKind }
  | { type: 'side'; side: Side; from: number; to: number; depth: number; kind: EdgeKind };

/** 직사각형(H×V) − 홈들 → 패널 바깥면 다각형 (화면 좌표 y↓, 시계방향 = 실내가 진행 방향 오른쪽) */
export function rectWithNotches(H: number, V: number, notches: Notch[]): Outline {
  const mid = (side: Side) => notches
    .filter((n): n is Extract<Notch, { type: 'side' }> => n.type === 'side' && n.side === side)
    .map(n => ({ s: n.from, e: n.to, d: n.depth, k: n.kind }))
    .sort((p, q) => p.s - q.s);
  const corner = (c: Corner) => {
    const n = notches.find((x): x is Extract<Notch, { type: 'corner' }> => x.type === 'corner' && x.corner === c);
    return n ? { a: n.w, b: n.h, k: n.kind } : null;
  };
  const tl = corner('topLeft'), tr = corner('topRight'), br = corner('bottomRight'), bl = corner('bottomLeft');
  const pts: Pt[] = [];
  const kinds: EdgeKind[] = [];
  // add(x, y, k): 꼭짓점 추가, k = 이 꼭짓점에서 시작하는 변 종류 (홈 안쪽 변은 홈 종류, 홈에서 나오는 변은 외벽)
  const add = (x: number, y: number, k: EdgeKind) => { pts.push({ x, y }); kinds.push(k); };
  if (tl) { add(0, tl.b, tl.k); add(tl.a, tl.b, tl.k); add(tl.a, 0, 'wall'); } else add(0, 0, 'wall');
  mid('top').forEach(n => { add(n.s, 0, n.k); add(n.s, n.d, n.k); add(n.e, n.d, n.k); add(n.e, 0, 'wall'); });
  if (tr) { add(H - tr.a, 0, tr.k); add(H - tr.a, tr.b, tr.k); add(H, tr.b, 'wall'); } else add(H, 0, 'wall');
  mid('right').forEach(n => { add(H, n.s, n.k); add(H - n.d, n.s, n.k); add(H - n.d, n.e, n.k); add(H, n.e, 'wall'); });
  if (br) { add(H, V - br.b, br.k); add(H - br.a, V - br.b, br.k); add(H - br.a, V, 'wall'); } else add(H, V, 'wall');
  [...mid('bottom')].reverse().forEach(n => { add(n.e, V, n.k); add(n.e, V - n.d, n.k); add(n.s, V - n.d, n.k); add(n.s, V, 'wall'); });
  if (bl) { add(bl.a, V, bl.k); add(bl.a, V - bl.b, bl.k); add(0, V - bl.b, 'wall'); } else add(0, V, 'wall');
  [...mid('left')].reverse().forEach(n => { add(0, n.e, n.k); add(n.d, n.e, n.k); add(n.d, n.s, n.k); add(0, n.s, 'wall'); });
  return cleanOutline({ pts, kinds });
}

/** 홈끼리 맞닿아 생긴 중복 꼭짓점·일직선(되돌아감 포함) 꼭짓점 제거 — 계단형 등 */
export function cleanOutline(o: Outline): Outline {
  let pts = [...o.pts], kinds = [...o.kinds];
  for (let changed = true; changed && pts.length > 3;) {
    changed = false;
    for (let i = 0; i < pts.length; i++) {
      const n = pts.length, p = pts[(i - 1 + n) % n], q = pts[i], r = pts[(i + 1) % n];
      const dup = q.x === r.x && q.y === r.y;
      const collinear = (p.x === q.x && q.x === r.x) || (p.y === q.y && q.y === r.y);
      if (dup || collinear) {
        // 꼭짓점 q 제거: 앞 변(p→q)이 q→r을 흡수 — 종류는 앞 변 것을 유지 (중복이면 q에서 시작하는 변 종류 유지)
        if (dup) { pts.splice((i + 1) % n, 1); kinds.splice((i + 1) % n, 1); }
        else { pts.splice(i, 1); kinds.splice(i, 1); }
        changed = true;
        break;
      }
    }
  }
  return { pts, kinds };
}

/** 하리 → 하리 홈 (빈 공간): 가운데형 = 폭 width−2PT × 깊이 height, 코너형 = (width−PT) × (height−PT) */
export function harisToNotches(haris: Hari[], PT: number): Notch[] {
  return haris.map((h): Notch => (h.hariType === 'corner'
    ? { type: 'corner', corner: h.corner as Corner, w: h.width - PT, h: (h.height ?? 0) - PT, kind: 'hari' }
    : { type: 'side', side: h.wallSide as Side, from: (h.position ?? 0) + PT, to: (h.position ?? 0) + h.width - PT, depth: h.height ?? 0, kind: 'hari' }));
}

/** 직사각형 + 하리 (기존 호환) */
export function rectWithHaris(H: number, V: number, PT: number, haris: Hari[]): Outline {
  return rectWithNotches(H, V, harisToNotches(haris, PT));
}

const sub = (p: Pt, q: Pt): Pt => ({ x: p.x - q.x, y: p.y - q.y });
const len = (p: Pt) => Math.hypot(p.x, p.y);
const unit = (p: Pt): Pt => { const l = len(p); return { x: p.x / l, y: p.y / l }; };
const along = (p: Pt, d: Pt, t: number): Pt => ({ x: p.x + d.x * t, y: p.y + d.y * t });

/**
 * 다각형(시계방향) → 패널 목록
 * flow: 패널 흐름 방향 (CCW = 반시계, 기본)
 */
export function layoutPolygon(outline: Outline, PT: number, flow: Flow, fill: FillOpts, openings: Opening[] = [],
  edits: PolyEdits = {}, pieceOut?: Record<string, PieceInfo>,
  doorEndsOut?: Map<Opening, { lo: 'yangsu' | 'eumsu'; hi: 'yangsu' | 'eumsu' }>): PolyPanel[] {
  const flipT = (t: 'yangsu' | 'eumsu'): 'yangsu' | 'eumsu' => (t === 'yangsu' ? 'eumsu' : 'yangsu');
  const n = outline.pts.length;
  const pts = flow === 'cw' ? outline.pts : [...outline.pts].reverse();
  // 반시계로 뒤집으면 변 j(pts[j]→pts[j+1])는 시계방향 변 (n−2−j)
  const kinds = flow === 'cw' ? outline.kinds : pts.map((_, j) => outline.kinds[(n - 2 - j + 2 * n) % n]);
  const dirs = pts.map((p, i) => unit(sub(pts[(i + 1) % n], p)));
  // 실내 방향 법선: 시계방향 진행이면 오른쪽, 반시계면 왼쪽 (화면 좌표 y↓)
  const inward = (d: Pt): Pt => (flow === 'cw' ? { x: -d.y, y: d.x } : { x: d.y, y: -d.x });
  // 꼭짓점 i (변 i−1 → 변 i) 볼록 여부: 시계 흐름은 오른쪽 꺾임, 반시계는 왼쪽 꺾임이 볼록
  const convex = (i: number) => {
    const din = dirs[(i - 1 + n) % n], dout = dirs[i];
    const cross = din.x * dout.y - din.y * dout.x;
    return flow === 'cw' ? cross > 0 : cross < 0;
  };
  // 외곽 경계(면 판정용)
  const xs = outline.pts.map(p => p.x), ys = outline.pts.map(p => p.y);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const sideOf = (a: Pt, b: Pt): Opening['side'] | null =>
    a.y === b.y ? (a.y === minY ? 'top' : a.y === maxY ? 'bottom' : null)
      : a.x === b.x ? (a.x === minX ? 'left' : a.x === maxX ? 'right' : null) : null;
  // 변 키 (양 끝 좌표 — 흐름과 무관) + 변별 개구부
  const keyOf = (i: number) => {
    const e1 = pts[i], e2 = pts[(i + 1) % n];
    return `${Math.min(e1.x, e2.x)},${Math.min(e1.y, e2.y)}-${Math.max(e1.x, e2.x)},${Math.max(e1.y, e2.y)}`;
  };
  const edgeOpenings = (i: number) => {
    const side = kinds[i] === 'wall' ? sideOf(pts[i], pts[(i + 1) % n]) : null;
    const k = keyOf(i);
    return openings.filter(o => (o.edge ? o.edge === k : !!side && o.side === side));
  };
  const axisOf = (i: number, p: Pt) => (dirs[i].x !== 0 ? p.x : p.y);
  // 기존 벽이 변 i의 꼭짓점 v를 덮는가 (그 꼭짓점에서 이웃 변이 기존 벽 면까지 끝까지 와야 함)
  const existingAt = (i: number, v: Pt) => edgeOpenings(i).some(o => o.kind === 'existing'
    && Math.min(o.from, o.to) <= axisOf(i, v) + 1 && Math.max(o.from, o.to) >= axisOf(i, v) - 1);

  const panels: PolyPanel[] = [];
  for (let i = 0; i < n; i++) {
    const d = dirs[i], nv = inward(d);
    const L = len(sub(pts[(i + 1) % n], pts[i]));
    const prevOpen = existingAt((i - 1 + n) % n, pts[i]);       // 앞 변이 이 꼭짓점에서 기존 벽
    const nextOpen = existingAt((i + 1) % n, pts[(i + 1) % n]); // 다음 변이 끝 꼭짓점에서 기존 벽
    // 꺾임 규칙: 시작 칸 소유(볼록 0 / 오목 −PT), 끝 칸은 다음 변 소유(볼록 −PT / 오목 0)
    // 이웃 변이 기존 벽이면 칸 소유자가 없으니 꼭짓점(기존 벽 면)까지 그대로 오고 음수로 마감
    // 이 변 자신이 꼭짓점에서 기존 벽이면 늘리거나 줄이지 않음 (그 구간은 어차피 비워짐 — 자투리 조각 방지)
    const s0 = prevOpen || existingAt(i, pts[i]) ? 0 : convex(i) ? 0 : -PT;
    const e0 = nextOpen || existingAt(i, pts[(i + 1) % n]) ? 0 : convex((i + 1) % n) ? -PT : 0;
    const stripLen = L - s0 + e0;
    const start = along(along(pts[i], d, s0), nv, PT / 2);
    const inDir = dirs[(i - 1 + n) % n];
    // 개구부로 띠를 조각냄 (흐름 방향 거리 t 기준), 조각 양끝의 마감 종류 기록
    const axis = (p: Pt) => (d.x !== 0 ? p.x : p.y);
    const ascending = d.x + d.y > 0; // 흐름이 좌표 오름차순인가
    // 문 끝 타입(흐름 기준): 자동이면 흐름 시작 쪽 음수·끝 쪽 양수, 지정이면 lo/hi를 흐름으로 환산
    const doorFlowEnds = (o: Opening) => {
      const lo = o.lo ?? (ascending ? 'eumsu' : 'yangsu'), hi = o.hi ?? (ascending ? 'yangsu' : 'eumsu');
      doorEndsOut?.set(o, { lo, hi });
      return { start: ascending ? lo : hi, end: ascending ? hi : lo };
    };
    type Cut = { a: number; b: number; kind: 'door' | 'existing'; o: Opening };
    const cuts: Cut[] = edgeOpenings(i).map(o => {
      const t1 = (o.from - axis(start)) / (d.x + d.y), t2 = (o.to - axis(start)) / (d.x + d.y); // d는 ±1 축 방향
      return { a: Math.max(0, Math.min(t1, t2)), b: Math.min(stripLen, Math.max(t1, t2)), kind: o.kind ?? 'door', o };
    }).filter(c => c.b > c.a).sort((p, q) => p.a - q.a);
    // 조각 양끝 마감: 꼭짓점이면 코너(기존 벽 이웃이면 음수)/양수, 기존 벽이면 음수, 문이면 문 끝의 반대 (문과 맞물림)
    const cutStartType = (c: Cut | null): EndType => (!c ? 'corner' : c.kind === 'existing' ? 'eumsu' : flipT(doorFlowEnds(c.o).end));
    const cutEndType = (c: Cut): EndType => (c.kind === 'existing' ? 'eumsu' : flipT(doorFlowEnds(c.o).start));
    const pieces: { p0: number; p1: number; startCut: Cut | null; endCut: Cut | null }[] = [];
    let cur = 0;
    let lastCut: Cut | null = null;
    cuts.forEach(c => { if (c.a > cur) pieces.push({ p0: cur, p1: c.a, startCut: lastCut, endCut: c }); else if (c.kind === 'door') doorFlowEnds(c.o); cur = Math.max(cur, c.b); lastCut = c; });
    if (cur < stripLen) pieces.push({ p0: cur, p1: stripLen, startCut: lastCut, endCut: null });
    const edgeKey = keyOf(i);
    pieces.forEach(({ p0, p1, startCut, endCut }, pk) => {
      const atStart = p0 === 0, atEnd = p1 === stripLen;
      // 조각 양끝 마감: 꼭짓점이면 코너(기존 벽 이웃이면 음수) / 양수, 개구부면 문=평평·기존 벽=음수
      const startEnd: EndType = atStart ? (prevOpen ? 'eumsu' : 'corner') : cutStartType(startCut);
      const endEnd: EndType = atEnd ? (nextOpen ? 'eumsu' : 'yangsu') : cutEndType(endCut!);
      const key = `${edgeKey}#${ascending ? pk : pieces.length - 1 - pk}`;
      const total = Math.round(p1 - p0);
      let error: string | undefined;
      // 외벽: 흐름과 무관하게 좌표 오름차순(가로 왼→오, 세로 위→아래)으로 채움 — 나머지가 오름차순 끝
      // 하리 안쪽 줄: 흐름 순서로 채움 — 흐름 시작부터 900, 나머지가 흐름 끝
      // 사용자 규칙이 있으면 규칙 결과(오름차순)로 채움
      let sizes = fillLength(total, fill);
      if (total < fill.min) error = `조각이 ${total}mm로 최소 ${fill.min}mm보다 짧음 (문·기존 벽이 꼭짓점에 너무 가까움)`;
      let inFlowOrder = kinds[i] === 'hari' || ascending; // true = 흐름 순서 그대로 배치
      const rule = edits.rules?.[key];
      if (rule) {
        const r = fillSegment(total, rule, { min: fill.min, max: fill.max, standard: fill.max, half: fill.half, space: fill.space });
        if (r.error) error = r.error;
        else { sizes = r.sizes; inFlowOrder = ascending; }
      }
      const ordered = inFlowOrder ? sizes : [...sizes].reverse();
      const nP = ordered.length;
      const single = nP === 1;
      // 흐름 순번 k ↔ 오름차순 순번
      const asc = (k: number) => (ascending ? k : nP - 1 - k);
      if (pieceOut) pieceOut[key] = { key, total, sizes: ascending ? ordered : [...ordered].reverse(), horiz: d.y === 0, error };
      let t = p0;
      const piecePanels: PolyPanel[] = [];
      ordered.forEach((size, k) => {
        // 간격은 각 패널 뒤 4 (1장이면 없음) — 역순 배치면 간격이 앞에 옴
        t += !single && !inFlowOrder ? fill.space : 0;
        const a = along(start, d, t), b = along(start, d, t + size);
        const first = k === 0, last = k === nP - 1;
        piecePanels.push({
          id: `${key}:${asc(k)}`, pieceKey: key, ascIndex: asc(k),
          edge: i, a, b, size,
          startType: first ? startEnd : 'eumsu',
          endType: last ? endEnd : 'yangsu',
          ...(first && startEnd === 'corner' ? { inDir } : {}),
        });
        t += size + (!single && inFlowOrder ? fill.space : 0);
      });
      // 접합 뒤집기: 흐름상 k와 k+1 사이 접합 (오름차순 접합 순번 = 두 패널 중 작은 오름차순 순번)
      for (let k = 0; k < nP - 1; k++) {
        const j = Math.min(asc(k), asc(k + 1));
        if (edits.flips?.[`${key}:j${j}`]) { piecePanels[k].endType = 'eumsu'; piecePanels[k + 1].startType = 'yangsu'; }
      }
      panels.push(...piecePanels);
    });
  }
  return panels;
}

// ══════════════════════════════════════════
// 칸막이 (실내 벽): 양 끝은 벽·기존 벽·다른 칸막이 면에 평평하게 맞댐(none — 칸막이 시작/끝에만 허용),
// 안쪽 접합은 양수/음수. 먼저 놓인 칸막이가 이어지고, 나중 칸막이는 그 면에서 끊겨 T자로 붙음 (십자 포함)
// ══════════════════════════════════════════
export interface Partition {
  id: string;
  dir: 'h' | 'v';       // h = 가로(y 고정), v = 세로(x 고정)
  pos: number;          // 중심선 좌표 (h면 y, v면 x)
  anchor?: number;      // 같은 줄에 실내 구간이 여러 개일 때(ㄷ자 등) 고를 기준 좌표 — 없으면 가장 긴 구간
  thick?: number;       // 칸막이 두께 (기본 PT)
}
export interface PartitionSeg { id: string; dir: 'h' | 'v'; pos: number; from: number; to: number; thick: number }

/** 칸막이 → 끊긴 구간들 (외곽선 안쪽, 벽 패널 안쪽 면 ~ 안쪽 면) */
export function partitionSegments(outline: Outline, PT: number, parts: Partition[], openings: Opening[] = []): PartitionSeg[] {
  const pts = outline.pts, n = pts.length;
  const keyOf = (a: Pt, b: Pt) => `${Math.min(a.x, b.x)},${Math.min(a.y, b.y)}-${Math.max(a.x, b.x)},${Math.max(a.y, b.y)}`;
  // 가로선(y=c)·세로선(x=c)과 외곽 변의 교차 → 실내 구간 (짝홀 규칙)
  const crossings = (dir: 'h' | 'v', c: number) => {
    const out: { t: number; edge: string; existing: boolean }[] = [];
    for (let i = 0; i < n; i++) {
      const a = pts[i], b = pts[(i + 1) % n];
      const perp = dir === 'v' ? a.y === b.y : a.x === b.x; // 줄과 수직인 변
      if (!perp) continue;
      const lo = dir === 'v' ? Math.min(a.x, b.x) : Math.min(a.y, b.y), hi = dir === 'v' ? Math.max(a.x, b.x) : Math.max(a.y, b.y);
      if (c <= lo || c >= hi) continue;
      const t = dir === 'v' ? a.y : a.x;
      const k = keyOf(a, b);
      const existing = openings.some(o => o.kind === 'existing' && o.edge === k && Math.min(o.from, o.to) <= c && Math.max(o.from, o.to) >= c);
      out.push({ t, edge: k, existing });
    }
    return out.sort((p, q) => p.t - q.t);
  };
  const segs: PartitionSeg[] = [];
  parts.forEach(p => {
    const thick = p.thick ?? PT;
    const xs = crossings(p.dir, p.pos);
    const spans: [number, number][] = [];
    for (let k = 0; k + 1 < xs.length; k += 2) {
      // 외곽선에서 벽 패널 두께만큼 안쪽 (기존 벽이면 외곽선 그대로)
      spans.push([xs[k].t + (xs[k].existing ? 0 : PT), xs[k + 1].t - (xs[k + 1].existing ? 0 : PT)]);
    }
    if (!spans.length) return;
    const span = p.anchor !== undefined
      ? spans.find(s => p.anchor! >= s[0] - PT && p.anchor! <= s[1] + PT) ?? spans[0]
      : spans.reduce((m, s) => (s[1] - s[0] > m[1] - m[0] ? s : m));
    // 먼저 놓인 수직 방향 칸막이 몸통에서 끊음 (십자·T자)
    const cuts = segs.filter(q => q.dir !== p.dir && q.from < p.pos && q.to > p.pos && q.pos > span[0] && q.pos < span[1])
      .map(q => [q.pos - q.thick / 2, q.pos + q.thick / 2] as [number, number]).sort((a, b) => a[0] - b[0]);
    let cur = span[0];
    const pieces: [number, number][] = [];
    cuts.forEach(([a, b]) => { if (a > cur) pieces.push([cur, a]); cur = Math.max(cur, b); });
    if (cur < span[1]) pieces.push([cur, span[1]]);
    pieces.forEach(([a, b], i) => { if (b - a > 1) segs.push({ id: `${p.id}#${i}`, dir: p.dir, pos: p.pos, from: a, to: b, thick }); });
  });
  return segs;
}

/** 칸막이 구간 → 패널 (좌표 오름차순으로 채움, 양 끝 none, 안쪽 접합 양수→음수) — 편집값(구간 규칙·접합 뒤집기) 적용 */
export function layoutPartitions(segs: PartitionSeg[], fill: FillOpts, edits: PolyEdits = {}, pieceOut?: Record<string, PieceInfo>): PolyPanel[] {
  const out: PolyPanel[] = [];
  segs.forEach(s => {
    const total = Math.round(s.to - s.from);
    const key = `part:${s.id}`;
    let sizes = fillLength(total, fill);
    let error: string | undefined = total < fill.min ? `칸막이 구간이 ${total}mm로 너무 짧음` : undefined;
    const rule = edits.rules?.[key];
    if (rule) {
      const r = fillSegment(total, rule, { min: fill.min, max: fill.max, standard: fill.max, half: fill.half, space: fill.space });
      if (r.error) error = r.error; else sizes = r.sizes;
    }
    if (pieceOut) pieceOut[key] = { key, total, sizes, horiz: s.dir === 'h', error };
    const single = sizes.length === 1;
    let t = s.from;
    const at = (v: number): Pt => (s.dir === 'h' ? { x: v, y: s.pos } : { x: s.pos, y: v });
    sizes.forEach((size, k) => {
      out.push({
        id: `${key}:${k}`, pieceKey: key, ascIndex: k, edge: -1,
        a: at(t), b: at(t + size), size,
        startType: k === 0 ? 'none' : 'eumsu',
        endType: k === sizes.length - 1 ? 'none' : 'yangsu',
        thick: s.thick,
      });
      t += size + (single ? 0 : fill.space);
    });
    // 접합 뒤집기 (오름차순 = 배치 순서)
    const mine = out.slice(out.length - sizes.length);
    for (let j = 0; j < mine.length - 1; j++) {
      if (edits.flips?.[`${key}:j${j}`]) { mine[j].endType = 'eumsu'; mine[j + 1].startType = 'yangsu'; }
    }
  });
  return out;
}
